// =============================================================================
// LP Express — Product Hero (PDP) template renderer.
//
// Renders the SECTION-BASED product-hero page (see
// shared/lp-express-product-hero.ts + shared/lp-express-sections.ts) as a
// flagship DTC physical-product sales page, ported from the template library's
// sales.product-hero: two-column hero (product gallery + buy box) → press/trust
// bar → benefit grid → how-it-works steps → reviews wall (REAL reviews via the
// shared review picker) → guarantee → FAQ accordion → closing dark CTA band →
// legal disclaimer → sticky bottom CTA bar (mobile).
//
// Unlike the earlier single-blob version, this component now renders ONE
// section block per invocation (selected via the `section` prop), so the
// page flows through the SAME ordered sections pipeline as long-form-v2:
// getLpSections order, reorder, per-section hide/delete, and per-section copy
// regeneration. Copy is read via `assembleLpProductHeroContent(generatedContent)`
// (which reads the per-section generatedContent keys — phBuyBox, phTrustBar, …)
// and edited in place through the SAME path-based `setText` ops the other
// templates use, but pointed at the NEW per-key paths (e.g.
// ["phBenefits","items",0,"title"], ["phBuyBox","headline"]).
//
//  • Imagery: the gallery LEADS with workflow-generated AI lifestyle shots
//    (generatedContent.productHeroImages — the main image defaults to the
//    first ready AI shot) and the LAST carousel slot is always the brand's
//    REAL product photo (override key "phBuyBox:gallery-main", swappable via
//    the media picker/upload, never AI-generated, with revert-to-default);
//    it doubles as the main image while the AI shots are still pending.
//  • Prices, ratings, review counts and discounts are ALWAYS derived from the
//    real product/offer/sale/review data — the generated copy never carries
//    dollar amounts, and strikethrough/discount framing only appears when a
//    real active discount exists on the run.
//  • Brand adaptation comes from the shared --color-brand-* CSS variables the
//    renderer already sets (brandCssVarsV2), so the same layout re-skins per
//    brand with readable contrast.
// =============================================================================
import { useState } from "react";
import {
  ChevronDown,
  Heart,
  Leaf,
  Loader2,
  Plus,
  RotateCw,
  ShieldCheck,
  Sparkles,
  Star,
  Truck,
  Zap,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  useLpEditor,
  LpImageActions,
  LpReviewActions,
  useLpMediaOverridesMap,
  useLpReviewOverride,
  type LpReviewChoice,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import type { LpSectionType } from "@shared/lp-express-sections";
import {
  assembleLpProductHeroContent,
  type LpProductHeroContent,
} from "@shared/lp-express-product-hero";

export type ProductHeroCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  bundleImage?: string | null;
  products?: Product[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  typography?: { heading?: string; body?: string } | any;
  /** In-app builder only — retries/regenerates a single AI lifestyle gallery
   *  slot (generatedContent.productHeroImages). Undefined on the public page,
   *  so failed slots stay hidden there (no retry affordance). */
  onRetrySlot?: (mergeKey: "productHeroImages", slotKey: string) => void;
};

// Shared brand palette (same CSS vars the quiz/listicle/advertorial templates
// use, set by the renderer root via brandCssVarsV2 → adapts per brand).
const HEADING = "var(--color-brand-navy)";
const ACCENT = "var(--color-brand-green)";
const DEEP = "var(--color-brand-deepnavy)";
const TEXT = "var(--color-brand-text)";
const MUTED = "var(--color-brand-muted)";
const CARD = "var(--color-brand-card)";
const BORDER = "var(--color-brand-lightborder)";
const GOLD = "var(--color-brand-gold)";
const CORAL = "var(--color-brand-coral)";
const PAGE_BG = "#FFFFFF";
const SOFT = "#F7F5EF";

// Media-override key for the gallery's single REAL product-photo slot (no AI
// regen on it; swap via the picker/upload — the AI lifestyle slots use their
// own GALLERY_AI_SLOT_PREFIX keys and DO support regenerate). It keeps the
// legacy "-main" key for override back-compat even though it now renders as
// the LAST carousel slot.
const GALLERY_MAIN_SLOT = "phBuyBox:gallery-main";
const GALLERY_THUMB_SLOT_PREFIX = "phBuyBox:gallery-thumb-";
const gallerySlotKey = (i: number): string =>
  i === 0 ? GALLERY_MAIN_SLOT : `${GALLERY_THUMB_SLOT_PREFIX}${i}`;
// Media-override key for an AI lifestyle slot (keyed by the slot's stable key
// from LP_PRODUCT_HERO_IMAGE_SLOTS, e.g. "phBuyBox:gallery-ai-lifestyle-1").
const GALLERY_AI_SLOT_PREFIX = "phBuyBox:gallery-ai-";
const galleryAiSlotKey = (slotKey: string): string =>
  `${GALLERY_AI_SLOT_PREFIX}${slotKey}`;

// Curated lucide icon set for the benefit grid — replaces the workflow's emoji
// `ic` field (house style is lucide icons, never emoji).
const BENEFIT_ICONS = [Leaf, ShieldCheck, Truck, Sparkles, Heart, Zap];

// ── Add-to-cart / CTA button ─────────────────────────────────────────────────
// Editor / no-URL → non-navigating span so copy stays editable.
function CtaButton({
  label,
  path,
  href,
  testId,
  size = "lg",
  variant = "solid",
}: {
  label: string;
  path: (string | number)[];
  href?: string;
  testId: string;
  size?: "lg" | "md";
  variant?: "solid" | "onDark";
}) {
  const editor = useLpEditor();
  const inner = (
    <EditableText
      value={label}
      path={path}
      as="span"
      noHideDelete
      placeholder="Add to cart"
      testId={`${testId}-label`}
    />
  );
  const style: React.CSSProperties = {
    display: "block",
    width: "100%",
    padding: size === "lg" ? "16px 0" : "13px 0",
    backgroundColor: variant === "onDark" ? "#FFFFFF" : ACCENT,
    color: variant === "onDark" ? DEEP : "#FFFFFF",
    fontSize: size === "lg" ? 17 : 15,
    fontWeight: 800,
    textAlign: "center",
    borderRadius: 12,
    letterSpacing: "0.02em",
    textDecoration: "none",
    boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
    cursor: "pointer",
  };
  if (editor || !href) {
    return (
      <span
        style={{ ...style, ...(editor ? {} : { opacity: 0.7, cursor: "not-allowed" }) }}
        data-testid={testId}
        data-lp-cta
      >
        {inner}
      </span>
    );
  }
  return (
    <a href={href} style={style} data-testid={testId} data-lp-cta>
      {inner}
    </a>
  );
}

function starRow(rating: number, size = 14) {
  return (
    <span style={{ display: "inline-flex", color: "#F5A623" }}>
      {Array.from({ length: Math.max(1, Math.min(5, rating)) }).map((_, s) => (
        <Star key={s} style={{ width: size, height: size }} className="fill-current" />
      ))}
    </span>
  );
}

// ── Product gallery (AI lifestyle shots + one real product photo last) ──────
// The primary image and each thumbnail are INDEPENDENT media slots: each
// supports Choose (media picker) + Upload + revert-to-default via
// LpImageActions. The carousel leads with the workflow-generated AI LIFESTYLE
// shots (generatedContent.productHeroImages) — the main image defaults to the
// first AI shot once its render lands — and the LAST slot is always the
// brand's real product photo. Each AI slot keys its spinner / retry state off
// its OWN slot status (never an aggregate flag) and supports regenerate (via
// onRetrySlot) + swap/upload/revert like any other slot.
type AiGalleryEntry = {
  key: string;
  url: string | null;
  status?: string;
  altText?: string;
};

function ProductGallery({ ctx }: { ctx: ProductHeroCtx }) {
  const overrides = useLpMediaOverridesMap();
  const editor = useLpEditor();
  const photos =
    ctx.bundleProductPhotos.length > 0
      ? ctx.bundleProductPhotos
      : ctx.bundleImage
        ? [ctx.bundleImage]
        : [];
  const [sel, setSel] = useState<number | null>(null);
  // The real product-photo slot resolves to its user override → the brand's
  // real product photo.
  const resolved = photos.map(
    (p, i) => (overrides?.[gallerySlotKey(i)]?.url as string | undefined) ?? p,
  );
  // AI lifestyle slots, leading the carousel. A slot resolves to its user
  // override → its generated image. Slots with nothing to show are kept only
  // in the BUILDER (onRetrySlot present): pending shows a live-updating
  // spinner, failed shows a retry tile. The public/published page is a static
  // snapshot with no polling, so pending/failed slots simply don't render
  // there (no infinite spinner).
  const aiRaw: any[] = Array.isArray(ctx.generatedContent?.productHeroImages)
    ? ctx.generatedContent.productHeroImages
    : [];
  const canRetry = !!(editor && ctx.onRetrySlot);
  const aiSlots: AiGalleryEntry[] = aiRaw
    .filter((s: any) => s && typeof s.key === "string" && s.key)
    .map((s: any) => ({
      key: s.key as string,
      url:
        (overrides?.[galleryAiSlotKey(s.key)]?.url as string | undefined) ??
        (s.imageStatus === "failed" ? null : (s.imageUrl as string | null)) ??
        null,
      status: s.imageStatus as string | undefined,
      altText: (s.altText as string | undefined) ?? undefined,
    }))
    .filter(
      (s) =>
        s.url ||
        (s.status === "pending" && !!ctx.onRetrySlot) ||
        (s.status === "failed" && canRetry),
    );
  // Selection indexes the COMBINED list: AI lifestyle slots FIRST, then the
  // single real product photo LAST.
  const realList = resolved.slice(0, 1);
  const realStart = aiSlots.length;
  const total = realStart + realList.length;
  // Default selection (before the user clicks a thumbnail): the first slot
  // with a visible image — an AI shot once its render lands, else the real
  // product photo while AI is still pending — so the main image never
  // defaults to a spinner.
  const firstAiWithUrl = aiSlots.findIndex((s) => !!s.url);
  const defaultIdx =
    firstAiWithUrl >= 0 ? firstAiWithUrl : realList.length > 0 ? realStart : 0;
  const mainIdx = sel !== null && sel < total ? sel : defaultIdx;
  const mainAi = mainIdx < realStart ? aiSlots[mainIdx] : undefined;
  const mainUrl = mainAi ? mainAi.url : (realList[mainIdx - realStart] ?? null);
  return (
    <div data-testid="product-hero-gallery">
      <div
        className="group/lpimg relative"
        style={{
          borderRadius: 16,
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
          border: `1px solid ${BORDER}`,
        }}
      >
        {mainUrl ? (
          <img
            src={mainUrl}
            alt={mainAi?.altText || ""}
            style={
              mainAi
                ? { display: "block", width: "100%", height: 460, objectFit: "cover" }
                : { display: "block", width: "100%", height: 460, objectFit: "contain", padding: 20 }
            }
            data-testid="img-product-hero-main"
          />
        ) : mainAi ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              height: 460,
              backgroundColor: SOFT,
            }}
            data-testid="img-product-hero-main"
          >
            {mainAi.status === "failed" ? (
              canRetry ? (
                <button
                  type="button"
                  onClick={() => ctx.onRetrySlot?.("productHeroImages", mainAi.key)}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold"
                  style={{ color: HEADING, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
                  data-testid={`button-retry-product-hero-ai-${mainAi.key}`}
                >
                  <RotateCw className="h-3.5 w-3.5" /> Retry lifestyle image
                </button>
              ) : (
                <Sparkles className="h-5 w-5" style={{ color: MUTED }} />
              )
            ) : (
              <>
                <Loader2 className="h-5 w-5 animate-spin" style={{ color: MUTED }} />
                <span style={{ fontSize: 12, color: MUTED }}>Generating lifestyle shot…</span>
              </>
            )}
          </div>
        ) : (
          <div style={{ height: 460, backgroundColor: SOFT }} data-testid="img-product-hero-main" />
        )}
        <LpImageActions
          slotKey={mainAi ? galleryAiSlotKey(mainAi.key) : gallerySlotKey(mainIdx)}
          canRegenerate={!!mainAi}
          onRegenerate={
            mainAi && ctx.onRetrySlot
              ? () => ctx.onRetrySlot?.("productHeroImages", mainAi.key)
              : undefined
          }
          productPhotos={ctx.bundleProductPhotos}
          revertLabel="Use default"
          revertTitle={
            mainAi
              ? "Revert to the AI lifestyle image"
              : "Revert to the default product photo"
          }
          testIdPrefix={
            mainAi ? `product-hero-ai-slot-${mainAi.key}` : `product-hero-slot-${mainIdx}`
          }
        />
      </div>
      {total > 1 && (
        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          {aiSlots.map((s, j) => {
            const i = j;
            return (
              <div
                key={`ai-${s.key}`}
                className="group/lpimg relative"
                onClick={() => setSel(i)}
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: 10,
                  overflow: "hidden",
                  border: `2px solid ${i === mainIdx ? ACCENT : BORDER}`,
                  backgroundColor: "#FFFFFF",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                data-testid={`button-product-hero-thumb-ai-${s.key}`}
              >
                {s.url ? (
                  <img
                    src={s.url}
                    alt={s.altText || ""}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      backgroundColor: SOFT,
                    }}
                  >
                    {s.status === "failed" ? (
                      <RotateCw className="h-3.5 w-3.5" style={{ color: MUTED }} />
                    ) : (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: MUTED }} />
                    )}
                  </div>
                )}
                {s.url && (
                  <LpImageActions
                    slotKey={galleryAiSlotKey(s.key)}
                    canRegenerate
                    onRegenerate={
                      ctx.onRetrySlot
                        ? () => ctx.onRetrySlot?.("productHeroImages", s.key)
                        : undefined
                    }
                    productPhotos={ctx.bundleProductPhotos}
                    revertLabel="Use default"
                    revertTitle="Revert to the AI lifestyle image"
                    testIdPrefix={`product-hero-ai-slot-${s.key}`}
                    className="!right-1 !top-1"
                  />
                )}
              </div>
            );
          })}
          {realList.map((url, i) => {
            const idx = realStart + i;
            return (
              <div
                key={`real-${i}`}
                className="group/lpimg relative"
                onClick={() => setSel(idx)}
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: 10,
                  overflow: "hidden",
                  border: `2px solid ${idx === mainIdx ? ACCENT : BORDER}`,
                  backgroundColor: "#FFFFFF",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                data-testid={`button-product-hero-thumb-${i}`}
              >
                <img
                  src={url}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
                />
                <LpImageActions
                  slotKey={gallerySlotKey(i)}
                  canRegenerate={false}
                  productPhotos={ctx.bundleProductPhotos}
                  revertLabel="Use default"
                  revertTitle="Revert to the default product photo"
                  testIdPrefix={`product-hero-slot-${i}`}
                  className="!right-1 !top-1"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Buy box ──────────────────────────────────────────────────────────────────
function BuyBox({
  content,
  ctx,
  headingFont,
}: {
  content: LpProductHeroContent;
  ctx: ProductHeroCtx;
  headingFont: string;
}) {
  const editor = useLpEditor();
  const receipt = ctx.receipt;
  const reviewDisplay = ctx.reviewDisplay;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  const showVariant = content.variantLabel && content.variantOptions.length > 0;
  return (
    <div data-testid="product-hero-buybox">
      {(content.eyebrow || editor) && (
        <EditableText
          value={content.eyebrow}
          path={["phBuyBox", "eyebrow"]}
          as="p"
          className="uppercase"
          style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", color: ACCENT, margin: "0 0 8px" }}
          placeholder="New · Limited release"
          testId="text-product-hero-eyebrow"
        />
      )}
      {(content.badge || editor) && (
        <EditableText
          value={content.badge}
          path={["phBuyBox", "badge"]}
          as="span"
          style={{
            display: "inline-block",
            fontSize: 11,
            fontWeight: 800,
            color: "#FFFFFF",
            backgroundColor: GOLD,
            padding: "3px 10px",
            borderRadius: 6,
            marginBottom: 10,
            letterSpacing: "0.04em",
          }}
          placeholder="Bestseller"
          testId="text-product-hero-badge"
        />
      )}
      <EditableText
        value={content.headline}
        path={["phBuyBox", "headline"]}
        as="h1"
        className={headingFont}
        style={{ fontSize: 34, fontWeight: 900, color: HEADING, lineHeight: 1.15, margin: "0 0 12px", letterSpacing: "-0.02em" }}
        placeholder="Product name / headline"
        testId="text-product-hero-headline"
      />
      {(content.subhead || editor) && (
        <EditableText
          value={content.subhead}
          path={["phBuyBox", "subhead"]}
          as="p"
          multiline
          style={{ fontSize: 17, lineHeight: 1.55, color: MUTED, margin: "0 0 16px" }}
          placeholder="Short product sub-line"
          testId="text-product-hero-subhead"
        />
      )}

      {/* Rating — DERIVED from real review data. */}
      {reviewDisplay?.hasReviews && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 16px" }} data-testid="product-hero-rating">
          {starRow(5, 16)}
          <span style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>
            {reviewDisplay.ratingText} · {reviewDisplay.reviewCountText} reviews
          </span>
        </div>
      )}

      {/* Buy-box benefit bullets */}
      {(content.heroBullets.length > 0 || editor) && (
        <div style={{ margin: "16px 0" }} data-testid="product-hero-bullets">
          {content.heroBullets.map((b, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 9 }}>
              <span style={{ color: ACCENT, fontWeight: 900, fontSize: 15, lineHeight: 1.5, flexShrink: 0 }}>✓</span>
              <EditableText
                value={b}
                path={["phBuyBox", "heroBullets", i]}
                as="span"
                multiline
                style={{ fontSize: 15, lineHeight: 1.5, color: TEXT }}
                placeholder="Benefit bullet"
                testId={`text-product-hero-bullet-${i}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Price — DERIVED from the receipt. */}
      {receipt && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "18px 0" }}>
          <span style={{ fontSize: 30, fontWeight: 900, color: HEADING }} data-testid="text-product-hero-price">
            {receipt.priceTodayLabel}
          </span>
          {receipt.hasDiscount && receipt.retailLabel && (
            <span style={{ fontSize: 17, color: "#999", textDecoration: "line-through" }}>{receipt.retailLabel}</span>
          )}
          {receipt.hasDiscount && receipt.discountPercent != null && (
            <span
              style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 11, padding: "3px 9px", borderRadius: 5, letterSpacing: "0.06em" }}
              data-testid="badge-product-hero-save"
            >
              SAVE {receipt.discountPercent}%
            </span>
          )}
        </div>
      )}

      {/* Optional variant selector (display-only). */}
      {showVariant && (
        <div style={{ margin: "14px 0" }} data-testid="product-hero-variant">
          <label style={{ fontSize: 12, fontWeight: 700, color: MUTED, display: "block", marginBottom: 6 }}>
            {content.variantLabel}
          </label>
          <div style={{ position: "relative" }}>
            <select
              aria-label={content.variantLabel}
              style={{
                width: "100%",
                padding: "12px 40px 12px 14px",
                borderRadius: 10,
                border: `1px solid ${BORDER}`,
                fontSize: 15,
                backgroundColor: "#fff",
                color: TEXT,
                appearance: "none",
                cursor: "pointer",
              }}
              data-testid="select-product-hero-variant"
            >
              {content.variantOptions.map((opt, i) => (
                <option key={i}>{opt}</option>
              ))}
            </select>
            <ChevronDown style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 18, height: 18, color: MUTED, pointerEvents: "none" }} />
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <CtaButton
          label={content.ctaText}
          path={["phBuyBox", "ctaText"]}
          href={checkoutUrl}
          testId="button-product-hero-cta"
        />
      </div>
      {(content.ctaSub || editor) && (
        <EditableText
          value={content.ctaSub}
          path={["phBuyBox", "ctaSub"]}
          as="p"
          style={{ fontSize: 12, color: MUTED, textAlign: "center", margin: "10px 0 0" }}
          placeholder="Free shipping · 30-day returns"
          testId="text-product-hero-cta-sub"
        />
      )}

      {/* Trust chips row */}
      {content.chips.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 18 }} data-testid="product-hero-chips">
          {content.chips.map((c, i) => (
            <span
              key={i}
              style={{ fontSize: 12, fontWeight: 600, color: TEXT, backgroundColor: SOFT, border: `1px solid ${BORDER}`, padding: "6px 12px", borderRadius: 9999 }}
            >
              <EditableText
                value={c}
                path={["phBuyBox", "chips", i]}
                as="span"
                placeholder="Free shipping"
                testId={`text-product-hero-chip-${i}`}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Review-wall card ─────────────────────────────────────────────────────────
// Real stored reviews AUTO-FILL the cards whenever the brand has them:
// precedence is picked override (generatedContent.reviewOverrides[slotKey]) >
// the Nth real review > the GENERATED item (fully editable). Generated copy is
// only a fallback so brands with zero stored reviews still get a full editable
// review block.
function ReviewCard({
  slot,
  item,
  options,
}: {
  slot: number;
  item?: { name: string; role: string; text: string; rating: number };
  options: LpReviewChoice[];
}) {
  const editor = useLpEditor();
  const slotKey = `phReviews:reviews:${slot}`;
  const override = useLpReviewOverride(slotKey);
  const rev: LpReviewChoice | null = override ?? options[slot] ?? null;
  const AVATARS = ["#E8927C", "#7BBFB2", "#A78BCA", "#E0A45E", "#8CB4D9", "#C4A265"];
  if (!rev && !item && !editor) return null;
  const name = rev ? rev.reviewer || "Verified customer" : item?.name ?? "";
  const rating = rev ? rev.rating : item?.rating ?? 5;
  const base: (string | number)[] = ["phReviews", "items", slot];
  return (
    <div
      className="group/lpreview relative"
      style={{ backgroundColor: CARD, borderRadius: 14, border: `1px solid ${BORDER}`, padding: "18px 20px" }}
      data-testid={`product-hero-review-${slot}`}
    >
      {options.length > 0 && (
        <LpReviewActions slotKey={slotKey} reviews={options} testIdPrefix={`product-hero-review-${slot}`} />
      )}
      <div style={{ marginBottom: 8 }}>{starRow(rating, 13)}</div>
      {rev ? (
        <p style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: "0 0 12px" }}>"{rev.reviewText}"</p>
      ) : (
        <EditableText
          value={item?.text ?? ""}
          path={[...base, "text"]}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: "0 0 12px" }}
          placeholder="Review quote"
          testId={`text-product-hero-review-text-${slot}`}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div
          style={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: AVATARS[slot % AVATARS.length], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{(name || "A")[0]}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          {rev ? (
            <p style={{ fontSize: 13, fontWeight: 700, color: HEADING, margin: 0 }}>{name}</p>
          ) : (
            <EditableText
              value={item?.name ?? ""}
              path={[...base, "name"]}
              as="p"
              style={{ fontSize: 13, fontWeight: 700, color: HEADING, margin: 0 }}
              placeholder="Customer name"
              testId={`text-product-hero-review-name-${slot}`}
            />
          )}
          {!rev && (item?.role || editor) && (
            <EditableText
              value={item?.role ?? ""}
              path={[...base, "role"]}
              as="p"
              style={{ fontSize: 11, color: MUTED, margin: 0 }}
              placeholder="Verified buyer"
              testId={`text-product-hero-review-role-${slot}`}
            />
          )}
          {rev && <p style={{ fontSize: 11, color: ACCENT, margin: 0, fontWeight: 600 }}>Verified buyer</p>}
        </div>
      </div>
    </div>
  );
}

// ── FAQ accordion item ───────────────────────────────────────────────────────
function FaqItem({
  index,
  item,
  open,
  onToggle,
}: {
  index: number;
  item: { q: string; a: string };
  open: boolean;
  onToggle: () => void;
}) {
  const editor = useLpEditor();
  const expanded = editor || open;
  const base: (string | number)[] = ["phFaq", "items", index];
  return (
    <div style={{ borderBottom: `1px solid ${BORDER}` }} data-testid={`product-hero-faq-${index}`}>
      <div
        onClick={editor ? undefined : onToggle}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 0", cursor: editor ? "default" : "pointer" }}
      >
        <EditableText
          value={item.q}
          path={[...base, "q"]}
          as="span"
          style={{ fontSize: 16, fontWeight: 700, color: HEADING }}
          placeholder="Question"
          testId={`text-product-hero-faq-q-${index}`}
        />
        {!editor && (
          <Plus
            style={{ width: 18, height: 18, color: MUTED, flexShrink: 0, transform: expanded ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}
          />
        )}
      </div>
      {expanded && (
        <EditableText
          value={item.a}
          path={[...base, "a"]}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.7, color: TEXT, margin: "0 0 16px" }}
          placeholder="Answer"
          testId={`text-product-hero-faq-a-${index}`}
        />
      )}
    </div>
  );
}

// ── Sticky bottom CTA bar (mobile) ──────────────────────────────────────────
function ProductHeroStickyBar({ content, ctx }: { content: LpProductHeroContent; ctx: ProductHeroCtx }) {
  const editor = useLpEditor();
  const receipt = ctx.receipt;
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  const bar = (
    <div
      className="lg:hidden"
      style={{ backgroundColor: DEEP, borderTop: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 -6px 24px rgba(0,0,0,0.25)" }}
      data-testid="product-hero-sticky-bar"
    >
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "9px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          {receipt?.hasDiscount && receipt.discountPercent != null && (
            <span className="uppercase" style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 900, fontSize: 10, letterSpacing: "0.1em", padding: "2px 7px", borderRadius: 4 }}>
              {receipt.discountPercent}% OFF
            </span>
          )}
          {receipt && <span style={{ color: "#fff", fontWeight: 900, fontSize: 16, lineHeight: 1 }}>{receipt.priceTodayLabel}</span>}
          {receipt?.hasDiscount && receipt.retailLabel && (
            <span style={{ color: "rgba(255,255,255,0.4)", textDecoration: "line-through", fontSize: 11 }}>{receipt.retailLabel}</span>
          )}
        </div>
        {editor || !checkoutUrl ? (
          <span style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "9px 22px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap" }} data-lp-cta>
            <EditableText value={content.stickyCtaText} path={["phStickyCta", "ctaText"]} as="span" noHideDelete placeholder="Add to cart" testId="button-product-hero-sticky-cta-label" />
          </span>
        ) : (
          <a href={checkoutUrl} style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "9px 22px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap", textDecoration: "none" }} data-testid="button-product-hero-sticky-cta" data-lp-cta>
            {content.stickyCtaText}
          </a>
        )}
      </div>
    </div>
  );
  // Editor: show the bar inline so its copy is editable without covering the
  // canvas. Public: pin it to the bottom.
  if (editor) return <div style={{ marginTop: 12 }}>{bar}</div>;
  return <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50 }}>{bar}</div>;
}

// ── Section heading helper ───────────────────────────────────────────────────
function SectionHeading({
  value,
  path,
  headingFont,
  placeholder,
  testId,
}: {
  value: string;
  path: (string | number)[];
  headingFont: string;
  placeholder: string;
  testId: string;
}) {
  return (
    <EditableText
      value={value}
      path={path}
      as="h2"
      className={headingFont}
      style={{ fontSize: 28, fontWeight: 800, color: HEADING, lineHeight: 1.25, margin: "0 0 8px", textAlign: "center", letterSpacing: "-0.01em" }}
      placeholder={placeholder}
      testId={testId}
    />
  );
}

// ── Per-section blocks ───────────────────────────────────────────────────────
// Hero: top nav (brand logo/name + shop-now CTA) + two-column gallery + buy box.
function BuyBoxSection({ content, ctx, headingFont }: { content: LpProductHeroContent; ctx: ProductHeroCtx; headingFont: string }) {
  const editor = useLpEditor();
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  const navCta = content.ctaText || "Shop now";
  return (
    <div style={{ backgroundColor: PAGE_BG }} data-testid="product-hero-page">
      {/* Top nav: brand logo/name + shop-now CTA */}
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "14px 16px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {ctx.logoUrl && <img src={ctx.logoUrl} alt="" style={{ width: 34, height: 34, objectFit: "contain" }} data-testid="img-product-hero-logo" />}
            <span style={{ fontSize: 17, fontWeight: 800, color: HEADING }}>{ctx.brand?.name ?? ""}</span>
          </div>
          {!editor && checkoutUrl && (
            <a
              href={checkoutUrl}
              style={{ fontSize: 13, fontWeight: 800, color: "#fff", backgroundColor: ACCENT, padding: "9px 18px", borderRadius: 9999, textDecoration: "none" }}
              data-testid="link-product-hero-nav-cta"
              data-lp-cta
            >
              {navCta}
            </a>
          )}
        </div>
      </div>

      {/* Hero: gallery + buy box */}
      <div className="section" style={{ padding: "32px 0" }}>
        <div
          className="grid grid-cols-1 lg:grid-cols-2"
          style={{ maxWidth: 1080, margin: "0 auto", padding: "0 20px", gap: 44, alignItems: "flex-start" }}
        >
          <ProductGallery ctx={ctx} />
          <BuyBox content={content} ctx={ctx} headingFont={headingFont} />
        </div>
      </div>
    </div>
  );
}

function TrustBarSection({ content, editor }: { content: LpProductHeroContent; editor: boolean }) {
  if (content.trust.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: "16px 20px" }} data-testid="product-hero-trust">
      <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 20 }}>
        {(content.trust.label || editor) && (
          <EditableText
            value={content.trust.label}
            path={["phTrustBar", "label"]}
            as="span"
            className="uppercase"
            style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: MUTED }}
            placeholder="As seen in"
            testId="text-product-hero-trust-label"
          />
        )}
        {content.trust.items.map((t, i) => (
          <EditableText
            key={i}
            value={t}
            path={["phTrustBar", "items", i]}
            as="span"
            style={{ fontSize: 15, fontWeight: 700, color: HEADING, opacity: 0.75 }}
            placeholder="Press name"
            testId={`text-product-hero-trust-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function BenefitsSection({ content, headingFont, editor }: { content: LpProductHeroContent; headingFont: string; editor: boolean }) {
  if (content.benefits.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "56px 20px" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <SectionHeading
          value={content.benefits.heading}
          path={["phBenefits", "heading"]}
          headingFont={headingFont}
          placeholder="Why you'll love it"
          testId="text-product-hero-benefits-heading"
        />
        {(content.benefits.dek || editor) && (
          <EditableText
            value={content.benefits.dek}
            path={["phBenefits", "dek"]}
            as="p"
            multiline
            style={{ fontSize: 16, lineHeight: 1.6, color: MUTED, margin: "0 auto 8px", textAlign: "center", maxWidth: 620 }}
            placeholder="Section intro"
            testId="text-product-hero-benefits-dek"
          />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: 22, marginTop: 30 }}>
          {content.benefits.items.map((b, i) => {
            const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
            return (
              <div
                key={i}
                style={{ backgroundColor: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 22px" }}
                data-testid={`product-hero-benefit-${i}`}
              >
                <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "rgba(45,122,79,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                  <Icon style={{ width: 22, height: 22, color: ACCENT }} />
                </div>
                <EditableText
                  value={b.title}
                  path={["phBenefits", "items", i, "title"]}
                  as="h3"
                  className={headingFont}
                  style={{ fontSize: 18, fontWeight: 800, color: HEADING, margin: "0 0 8px" }}
                  placeholder="Benefit title"
                  testId={`text-product-hero-benefit-title-${i}`}
                />
                <EditableText
                  value={b.body}
                  path={["phBenefits", "items", i, "body"]}
                  as="p"
                  multiline
                  style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: 0 }}
                  placeholder="Benefit body"
                  testId={`text-product-hero-benefit-body-${i}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HowItWorksSection({ content, headingFont, editor }: { content: LpProductHeroContent; headingFont: string; editor: boolean }) {
  if (content.how.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "56px 20px" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <SectionHeading
          value={content.how.heading}
          path={["phHowItWorks", "heading"]}
          headingFont={headingFont}
          placeholder="How it works"
          testId="text-product-hero-how-heading"
        />
        <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: 24, marginTop: 30 }}>
          {content.how.items.map((s, i) => (
            <div key={i} style={{ textAlign: "center" }} data-testid={`product-hero-step-${i}`}>
              <div style={{ width: 46, height: 46, borderRadius: "50%", backgroundColor: HEADING, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: "#fff" }}>{i + 1}</span>
              </div>
              <EditableText
                value={s.title}
                path={["phHowItWorks", "items", i, "title"]}
                as="h3"
                className={headingFont}
                style={{ fontSize: 18, fontWeight: 800, color: HEADING, margin: "0 0 8px" }}
                placeholder="Step title"
                testId={`text-product-hero-step-title-${i}`}
              />
              <EditableText
                value={s.body}
                path={["phHowItWorks", "items", i, "body"]}
                as="p"
                multiline
                style={{ fontSize: 14, lineHeight: 1.65, color: TEXT, margin: 0 }}
                placeholder="Step body"
                testId={`text-product-hero-step-body-${i}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewsSection({ content, ctx, headingFont }: { content: LpProductHeroContent; ctx: ProductHeroCtx; headingFont: string }) {
  const reviewDisplay = ctx.reviewDisplay;
  const reviewOptions = (reviewDisplay?.allReviews ?? []) as LpReviewChoice[];
  const reviewItems = Array.isArray(content.reviews.items) ? content.reviews.items : [];
  if (reviewItems.length === 0 && reviewOptions.length === 0) return null;
  return (
    <EditableElement elementKey="product-hero-reviews" toolbarSide="left">
      <div style={{ backgroundColor: PAGE_BG, padding: "56px 20px" }} data-testid="product-hero-reviews">
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <SectionHeading
            value={content.reviews.heading}
            path={["phReviews", "heading"]}
            headingFont={headingFont}
            placeholder="What customers are saying"
            testId="text-product-hero-reviews-heading"
          />
          {reviewDisplay?.hasReviews && (
            <p style={{ fontSize: 15, color: MUTED, textAlign: "center", margin: "0 0 8px" }} data-testid="text-product-hero-reviews-agg">
              <span style={{ color: "#F5A623" }}>★★★★★</span> {reviewDisplay.ratingText} · {reviewDisplay.reviewCountText} reviews
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 18, marginTop: 28 }}>
            {Array.from({
              length: Math.min(3, Math.max(reviewItems.length, reviewOptions.length)),
            }).map((_, i) => (
              <ReviewCard key={i} slot={i} item={reviewItems[i]} options={reviewOptions} />
            ))}
          </div>
        </div>
      </div>
    </EditableElement>
  );
}

function GuaranteeSection({ content, headingFont, editor }: { content: LpProductHeroContent; headingFont: string; editor: boolean }) {
  if (!content.guarantee.heading && !content.guarantee.body && !editor) return null;
  return (
    <div style={{ backgroundColor: SOFT, padding: "48px 20px" }}>
      <div
        style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 24, backgroundColor: CARD, border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: "28px 26px", flexWrap: "wrap" }}
        data-testid="product-hero-guarantee"
      >
        <div
          style={{ width: 96, height: 96, borderRadius: "50%", backgroundColor: "rgba(45,122,79,0.1)", border: `2px solid ${ACCENT}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <span style={{ fontSize: 22, fontWeight: 900, color: ACCENT, lineHeight: 1 }}>{content.guarantee.sealTop || "30"}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT, textTransform: "uppercase", letterSpacing: "0.06em" }}>{content.guarantee.sealBottom || "Day"}</span>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <EditableText
            value={content.guarantee.heading}
            path={["phGuarantee", "heading"]}
            as="h3"
            className={headingFont}
            style={{ fontSize: 21, fontWeight: 800, color: HEADING, margin: "0 0 8px" }}
            placeholder="Our guarantee"
            testId="text-product-hero-guarantee-heading"
          />
          <EditableText
            value={content.guarantee.body}
            path={["phGuarantee", "body"]}
            as="p"
            multiline
            style={{ fontSize: 14, lineHeight: 1.7, color: TEXT, margin: 0 }}
            placeholder="Guarantee body"
            testId="text-product-hero-guarantee-body"
          />
        </div>
      </div>
    </div>
  );
}

function FaqSection({ content, headingFont, editor }: { content: LpProductHeroContent; headingFont: string; editor: boolean }) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  if (content.faq.items.length === 0 && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, padding: "56px 20px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SectionHeading
          value={content.faq.heading}
          path={["phFaq", "heading"]}
          headingFont={headingFont}
          placeholder="Frequently asked questions"
          testId="text-product-hero-faq-heading"
        />
        <div style={{ marginTop: 22 }}>
          {content.faq.items.map((f, i) => (
            <FaqItem
              key={i}
              index={i}
              item={f}
              open={openFaq === i}
              onToggle={() => setOpenFaq((cur) => (cur === i ? null : i))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FinalCtaSection({ content, ctx, headingFont, editor }: { content: LpProductHeroContent; ctx: ProductHeroCtx; headingFont: string; editor: boolean }) {
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;
  return (
    <div style={{ backgroundColor: DEEP, padding: "64px 20px" }} data-testid="product-hero-final">
      <div style={{ maxWidth: 620, margin: "0 auto", textAlign: "center" }}>
        <EditableText
          value={content.final.heading}
          path={["phFinalCta", "heading"]}
          as="h2"
          className={headingFont}
          style={{ fontSize: 30, fontWeight: 900, color: "#fff", lineHeight: 1.2, margin: "0 0 12px", letterSpacing: "-0.01em" }}
          placeholder="Ready to try it?"
          testId="text-product-hero-final-heading"
        />
        {(content.final.dek || editor) && (
          <EditableText
            value={content.final.dek}
            path={["phFinalCta", "dek"]}
            as="p"
            multiline
            style={{ fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,0.8)", margin: "0 0 24px" }}
            placeholder="Closing sub-line"
            testId="text-product-hero-final-dek"
          />
        )}
        <div style={{ maxWidth: 320, margin: "0 auto" }}>
          <CtaButton
            label={content.final.ctaText || content.ctaText}
            path={["phFinalCta", "ctaText"]}
            href={checkoutUrl}
            testId="button-product-hero-final-cta"
            variant="onDark"
          />
        </div>
      </div>
    </div>
  );
}

function DisclaimerSection({ content, editor }: { content: LpProductHeroContent; editor: boolean }) {
  if (!content.disclaimer && !editor) return null;
  return (
    <div style={{ backgroundColor: PAGE_BG, borderTop: `1px solid ${BORDER}`, padding: "24px 20px" }}>
      <EditableText
        value={content.disclaimer}
        path={["phDisclaimer", "text"]}
        as="p"
        multiline
        style={{ maxWidth: 760, margin: "0 auto", fontSize: 11, lineHeight: 1.6, color: MUTED, textAlign: "center" }}
        placeholder="Legal disclaimer"
        testId="text-product-hero-disclaimer"
      />
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
// Renders ONE product-hero section block, selected via `section`. All blocks
// read the whole-page copy shape assembled from the per-section generatedContent
// keys, and edit in place through the NEW per-key paths.
export default function LpExpressProductHero({
  section,
  ctx,
}: {
  section: LpSectionType;
  ctx: ProductHeroCtx;
}) {
  const editor = !!useLpEditor();
  const content = assembleLpProductHeroContent(ctx.generatedContent);
  const headingFont = ctx.typography?.heading ?? "";
  const bodyFont = ctx.typography?.body ?? "";
  const wrap = (node: React.ReactNode) => <div className={bodyFont}>{node}</div>;

  switch (section) {
    case "phBuyBox":
      return wrap(<BuyBoxSection content={content} ctx={ctx} headingFont={headingFont} />);
    case "phTrustBar":
      return wrap(<TrustBarSection content={content} editor={editor} />);
    case "phBenefits":
      return wrap(<BenefitsSection content={content} headingFont={headingFont} editor={editor} />);
    case "phHowItWorks":
      return wrap(<HowItWorksSection content={content} headingFont={headingFont} editor={editor} />);
    case "phReviews":
      return wrap(<ReviewsSection content={content} ctx={ctx} headingFont={headingFont} />);
    case "phGuarantee":
      return wrap(<GuaranteeSection content={content} headingFont={headingFont} editor={editor} />);
    case "phFaq":
      return wrap(<FaqSection content={content} headingFont={headingFont} editor={editor} />);
    case "phFinalCta":
      return wrap(<FinalCtaSection content={content} ctx={ctx} headingFont={headingFont} editor={editor} />);
    case "phDisclaimer":
      return wrap(<DisclaimerSection content={content} editor={editor} />);
    case "phStickyCta":
      return wrap(<ProductHeroStickyBar content={content} ctx={ctx} />);
    default:
      return null;
  }
}
